/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useContext, useEffect, useState } from 'react';
import { MinusSquareOutlined, PlusSquareOutlined } from '@ant-design/icons';
import { ColumnProps } from 'antd/lib/table';

import ActivityNote from '@totem/components/ticketing/details/activities/activityNote';
import {
  Activity,
  TicketQueryResult,
} from '@totem/components/ticketing/ticketing';
import IBTable from '@totem/components/ui_components/antDesignOverrides/IBTable';
import { permissions } from '@totem/components/userContext/security/permissions/permissions';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import { formatDate } from '@totem/utilities/common';

import '../../ticketing.css';

type Props = {
  ticket: TicketQueryResult;
};

const ActivityTable = ({ ticket }: Props) => {
  const { userProfile } = useContext(UserProfileContext);
  const [typeFilter, setTypeFilter] = useState<string[]>([]);
  const [createdByFilter, setCreatedByFilter] = useState<string[]>([]);
  const [expandedRowKeys, setExpandedRowKeys] = useState<string[]>([]);
  const uniqueActivityCodes = () => {
    const activityCodes = [];

    if (ticket.ticket.activity !== null) {
      ticket.ticket.activity.filter((item) => {
        const idx = activityCodes.findIndex((srch) => srch.id === item.type.id);
        if (idx <= -1) {
          activityCodes.push(item.type);
        }
        return null;
      });
    }
    return activityCodes;
  };

  const getActivityTypeFilter = () => {
    const filterOptions = [];
    const activityCodes = uniqueActivityCodes();
    if (activityCodes !== null && activityCodes.length > 0) {
      activityCodes.forEach((ac) => {
        filterOptions.push({
          text: ac.display,
          value: ac.id,
        });
      });
    }
    return filterOptions;
  };

  const uniqueCreatedByNames = () => {
    const createdByOptions = [];

    if (ticket.ticket.activity !== null) {
      ticket.ticket.activity.filter((item) => {
        const idx = createdByOptions.findIndex(
          (srch) => srch === item.createdByName,
        );
        if (idx <= -1) {
          createdByOptions.push(item.createdByName);
        }
        return null;
      });
    }
    return createdByOptions;
  };

  const getCreatedByNameFilter = () => {
    const filterOptions = [];
    const createdByOptions = uniqueCreatedByNames();
    if (createdByOptions !== null && createdByOptions.length > 0) {
      createdByOptions.forEach((cb) => {
        filterOptions.push({
          text: cb,
          value: cb,
        });
      });
    }
    return filterOptions;
  };

  const getActivity = () => {
    if (ticket !== null) {
      if (ticket.ticket !== null) {
        let activity = ticket.ticket.activity.filter(
          (chk) =>
            (chk.type.code !== 'AUDIT' && !chk.isPrivate) ||
            permissions.internal.hasPermissions(userProfile, null),
        );
        if (typeFilter.length > 0) {
          activity = activity.filter(
            (rec) => typeFilter.indexOf(rec.type.id) >= 0,
          );
        }
        if (createdByFilter.length > 0) {
          activity = activity.filter(
            (rec) => createdByFilter.indexOf(rec.createdByName) >= 0,
          );
        }
        return activity.sort((aActivity, bActivity) =>
          aActivity.createdAt < bActivity.createdAt ? 1 : -1,
        );
      }
    }
    return [];
  };

  const activity = getActivity();
  const activityIds = activity.map((item) => item.id);

  useEffect(() => {
    setExpandedRowKeys((prev) => {
      const newKeys = activityIds.filter((id) => !prev.includes(id));
      return newKeys.length > 0 ? [...prev, ...newKeys] : prev;
    });
  }, [ticket]);

  const allExpanded =
    activityIds.length > 0 &&
    activityIds.every((id) => expandedRowKeys.includes(id));

  const handleToggleExpandAll = () => {
    setExpandedRowKeys(allExpanded ? [] : activityIds);
  };

  const columns: ColumnProps<Activity>[] = [
    {
      title: 'Created',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (_, activityRecord: Activity) =>
        formatDate(activityRecord.createdAt),
      sorter: (compA, compB) =>
        compA.createdAt === compB.createdAt
          ? 0
          : compA.createdAt > compB.createdAt
            ? 1
            : -1,
      showSorterTooltip: true,
      sortDirections: ['ascend', 'descend'],
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      render: (_, activityRecord: Activity) => activityRecord.type.display,
      showSorterTooltip: true,
      sortDirections: ['ascend', 'descend'],
      sorter: (compA, compB) =>
        compA.type.display === compB.type.display
          ? 0
          : compA.type.display > compB.type.display
            ? 1
            : -1,
      filters: getActivityTypeFilter(),
    },
    {
      title: 'Created By',
      dataIndex: 'createdByName',
      key: 'createdByName',
      showSorterTooltip: true,
      sortDirections: ['ascend', 'descend'],
      sorter: (compA, compB) =>
        compA.createdByName === compB.createdByName
          ? 0
          : compA.createdByName > compB.createdByName
            ? 1
            : -1,
      filters: getCreatedByNameFilter(),
    },
  ];

  const handleTableChange = (pagination, filters, sorter) => {
    if (filters.type !== null) {
      setTypeFilter(filters.type);
    } else {
      setTypeFilter([]);
    }
    if (filters.createdByName !== null) {
      setCreatedByFilter(filters.createdByName);
    } else {
      setCreatedByFilter([]);
    }
  };

  // @ts-ignore
  return (
    <IBTable
      showSorterTooltip
      columns={columns}
      dataSource={activity}
      expandable={{
        expandedRowRender: (activityRecord) => (
          <ActivityNote
            note={activityRecord.notes}
            attributes={activityRecord.attributes}
          />
        ),
        expandedRowKeys,
        onExpandedRowsChange: (keys) => setExpandedRowKeys(keys as string[]),
        columnTitle: allExpanded ? (
          <MinusSquareOutlined
            title="Collapse All"
            onClick={handleToggleExpandAll}
            style={{ cursor: 'pointer' }}
          />
        ) : (
          <PlusSquareOutlined
            title="Expand All"
            onClick={handleToggleExpandAll}
            style={{ cursor: 'pointer' }}
          />
        ),
      }}
      loading={false}
      onChange={handleTableChange}
      rowKey="id"
    />
  );
};

export default ActivityTable;
